// src/pages/PrivacyPolicyPage.jsx - Enhanced Black & White Design
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Shield, ArrowLeft, Mail, Globe, Lock, Eye, Database } from 'lucide-react';
import { useLocalizedNavigate } from '../hooks/useLocalizedNavigate';
import { useScrollAnimation } from '../hooks/useScrollAnimation';

const PrivacyPolicyPage = () => {
  const { t } = useTranslation();
  const navigate = useLocalizedNavigate();

  // Scroll animations
  const [headerRef, headerVisible] = useScrollAnimation();
  const [contentRef, contentVisible] = useScrollAnimation();
  const [ctaRef, ctaVisible] = useScrollAnimation();

  return (
    <div className="min-h-screen bg-white">
      <div className="relative z-10 max-w-4xl mx-auto px-4 py-12">
        {/* Back Button - Enhanced */}
        <button
          onClick={() => navigate(-1)}
          className="group flex items-center gap-2 text-black/60 hover:text-black mb-8 transition-all duration-300 font-medium"
        >
          <ArrowLeft className="w-5 h-5 transition-transform duration-300 group-hover:-translate-x-1" />
          <span>{t('legal.back')}</span>
        </button>

        {/* Header - Enhanced */}
        <div
          ref={headerRef}
          className={`
            relative bg-white rounded-2xl border-[3px] border-black shadow-2xl p-8 mb-8 overflow-hidden
            transition-all duration-1000
            ${headerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}
          `}
        >
          {/* Decorative corners */}
          <div className="absolute top-0 left-0 w-16 h-16 border-t-4 border-l-4 border-black/10 rounded-tl-2xl" />
          <div className="absolute bottom-0 right-0 w-16 h-16 border-b-4 border-r-4 border-black/10 rounded-br-2xl" />

          <div className="relative z-10 flex items-start gap-6">
            {/* Icon */}
            <div className="flex-shrink-0 w-16 h-16 bg-black rounded-2xl flex items-center justify-center shadow-lg">
              <Shield className="w-8 h-8 text-white" />
            </div>

            {/* Title */}
            <div className="flex-1">
              <h1 className="font-space text-3xl sm:text-4xl font-black text-black mb-2 tracking-tight">
                {t('legal.privacy.title')}
              </h1>
              <p className="text-black/60 font-medium">
                {t('legal.lastUpdated')}: October 12, 2025
              </p>
              <p className="text-black/60 text-sm mt-2">
                {t('footer.disclaimer')}
              </p>
            </div>
          </div>
        </div>

        {/* Content - Enhanced */}
        <div
          ref={contentRef}
          className="relative bg-white rounded-2xl border-[3px] border-black shadow-2xl p-8 mb-8 overflow-hidden"
        >
          {/* Background gradient */}
          <div className="absolute inset-0 bg-gradient-to-br from-gray-50 via-white to-gray-50 opacity-50" />

          <div className="relative z-10 prose prose-lg max-w-none">
            {/* Introduction */}
            <section
              className={`
                mb-10 pb-8 border-b border-black/10
                transition-all duration-700
                ${contentVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}
              `}
            >
              <h2 className="font-space text-2xl font-black text-black tracking-tight mb-4">
                {t('legal.privacy.intro.title')}
              </h2>
              <p className="text-black/70 leading-relaxed mb-4">
                {t('legal.privacy.intro.text1')}
              </p>
              <p className="text-black/70 leading-relaxed">
                {t('legal.privacy.intro.text2')}
              </p>
            </section>

            {/* 1. Data Controller */}
            <section
              className={`
                mb-10 pb-8 border-b border-black/10
                transition-all duration-700 delay-100
                ${contentVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}
              `}
            >
              <div className="flex items-start gap-4 mb-4">
                <div className="flex-shrink-0 w-10 h-10 bg-black text-white rounded-lg flex items-center justify-center font-bold">
                  1
                </div>
                <h2 className="font-space text-2xl font-black text-black tracking-tight flex-1">
                  {t('legal.privacy.controller.title')}
                </h2>
              </div>

              <div className="relative bg-white border-2 border-black/10 rounded-xl p-6 group hover:border-black transition-all duration-300">
                <p className="font-bold text-black mb-4 text-lg">Logami</p>

                <div className="space-y-3">
                  <div className="flex items-center gap-3 text-black/70">
                    <div className="w-8 h-8 bg-black/5 rounded-lg flex items-center justify-center group-hover:bg-black group-hover:text-white transition-all duration-300">
                      <Mail className="w-4 h-4" />
                    </div>
                    <span className="font-medium">team@logami.app</span>
                  </div>

                  <div className="flex items-center gap-3 text-black/70">
                    <div className="w-8 h-8 bg-black/5 rounded-lg flex items-center justify-center group-hover:bg-black group-hover:text-white transition-all duration-300">
                      <Globe className="w-4 h-4" />
                    </div>
                    <span className="font-medium">https://logami.app</span>
                  </div>

                  <div className="flex items-start gap-3 text-black/70 pt-2 border-t border-black/10 mt-3">
                    <div className="w-8 h-8 bg-black/5 rounded-lg flex items-center justify-center group-hover:bg-black group-hover:text-white transition-all duration-300 flex-shrink-0">
                      <Lock className="w-4 h-4" />
                    </div>
                    <span className="font-medium">{t('legal.privacy.controller.location')}</span>
                  </div>
                </div>

                <div className="absolute top-3 right-3 w-3 h-3 bg-black/10 rounded-full group-hover:bg-black transition-colors duration-300" />
              </div>
            </section>

            {/* 2. Data We Collect */}
            <section
              className={`
                mb-10 pb-8 border-b border-black/10
                transition-all duration-700 delay-200
                ${contentVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}
              `}
            >
              <div className="flex items-start gap-4 mb-6">
                <div className="flex-shrink-0 w-10 h-10 bg-black text-white rounded-lg flex items-center justify-center font-bold">
                  2
                </div>
                <h2 className="font-space text-2xl font-black text-black tracking-tight flex-1">
                  {t('legal.privacy.dataCollected.title')}
                </h2>
              </div>

              {/* Account Data */}
              <div className="mb-6">
                <h3 className="text-xl font-bold text-black mb-3 flex items-center gap-2">
                  <Database className="w-5 h-5" />
                  {t('legal.privacy.dataCollected.account.title')}
                </h3>
                <ul className="space-y-3">
                  {[1, 2, 3].map(i => (
                    <li key={i} className="flex items-start gap-3 group">
                      <div className="flex-shrink-0 w-6 h-6 bg-black/5 rounded-full flex items-center justify-center mt-0.5 group-hover:bg-black group-hover:text-white transition-colors duration-300">
                        <span className="text-xs font-bold">✓</span>
                      </div>
                      <span className="text-black/70 leading-relaxed flex-1">
                        {t(`legal.privacy.dataCollected.account.item${i}`)}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Usage Data */}
              <div className="mb-6">
                <h3 className="text-xl font-bold text-black mb-3 flex items-center gap-2">
                  <Eye className="w-5 h-5" />
                  {t('legal.privacy.dataCollected.usage.title')}
                </h3>
                <ul className="space-y-3">
                  {[1, 2, 3, 4].map(i => (
                    <li key={i} className="flex items-start gap-3 group">
                      <div className="flex-shrink-0 w-6 h-6 bg-black/5 rounded-full flex items-center justify-center mt-0.5 group-hover:bg-black group-hover:text-white transition-colors duration-300">
                        <span className="text-xs font-bold">✓</span>
                      </div>
                      <span className="text-black/70 leading-relaxed flex-1">
                        {t(`legal.privacy.dataCollected.usage.item${i}`)}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Technical Data */}
              <div>
                <h3 className="text-xl font-bold text-black mb-3 flex items-center gap-2">
                  <Lock className="w-5 h-5" />
                  {t('legal.privacy.dataCollected.technical.title')}
                </h3>
                <ul className="space-y-3">
                  {[1, 2, 3].map(i => (
                    <li key={i} className="flex items-start gap-3 group">
                      <div className="flex-shrink-0 w-6 h-6 bg-black/5 rounded-full flex items-center justify-center mt-0.5 group-hover:bg-black group-hover:text-white transition-colors duration-300">
                        <span className="text-xs font-bold">✓</span>
                      </div>
                      <span className="text-black/70 leading-relaxed flex-1">
                        {t(`legal.privacy.dataCollected.technical.item${i}`)}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </section>

            {/* 3. Legal Basis */}
            <section
              className={`
                mb-10 pb-8 border-b border-black/10
                transition-all duration-700 delay-300
                ${contentVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}
              `}
            >
              <div className="flex items-start gap-4 mb-4">
                <div className="flex-shrink-0 w-10 h-10 bg-black text-white rounded-lg flex items-center justify-center font-bold">
                  3
                </div>
                <h2 className="font-space text-2xl font-black text-black tracking-tight flex-1">
                  {t('legal.privacy.legalBasis.title')}
                </h2>
              </div>

              <p className="text-black/70 leading-relaxed mb-4">
                {t('legal.privacy.legalBasis.text')}
              </p>

              <ul className="space-y-3">
                {['consent', 'contract', 'legitimate'].map((type, index) => (
                  <li key={type} className="flex items-start gap-3 group">
                    <div className="flex-shrink-0 w-6 h-6 bg-black/5 rounded-full flex items-center justify-center mt-0.5 group-hover:bg-black group-hover:text-white transition-colors duration-300">
                      <span className="text-xs font-bold">✓</span>
                    </div>
                    <span className="text-black/70 leading-relaxed flex-1">
                      <strong className="text-black">{t(`legal.privacy.legalBasis.${type}.title`)}:</strong>{' '}
                      {t(`legal.privacy.legalBasis.${type}.text`)}
                    </span>
                  </li>
                ))}
              </ul>
            </section>

            {/* 4-6: Simplified sections */}
            {[
              { num: 4, key: 'usage', items: 5 },
              { num: 5, key: 'sharing', special: true },
              { num: 6, key: 'retention', items: 0 }
            ].map((section, idx) => (
              <section
                key={section.key}
                className={`
                  mb-10 pb-8 border-b border-black/10
                  transition-all duration-700
                  ${contentVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}
                `}
                style={{ transitionDelay: contentVisible ? `${(idx + 4) * 0.1}s` : '0s' }}
              >
                <div className="flex items-start gap-4 mb-4">
                  <div className="flex-shrink-0 w-10 h-10 bg-black text-white rounded-lg flex items-center justify-center font-bold">
                    {section.num}
                  </div>
                  <h2 className="font-space text-2xl font-black text-black tracking-tight flex-1">
                    {t(`legal.privacy.${section.key}.title`)}
                  </h2>
                </div>

                <p className="text-black/70 leading-relaxed mb-4">
                  {t(`legal.privacy.${section.key}.text`)}
                </p>

                {section.items > 0 && (
                  <ul className="space-y-3">
                    {[...Array(section.items)].map((_, i) => (
                      <li key={i} className="flex items-start gap-3 group">
                        <div className="flex-shrink-0 w-6 h-6 bg-black/5 rounded-full flex items-center justify-center mt-0.5 group-hover:bg-black group-hover:text-white transition-colors duration-300">
                          <span className="text-xs font-bold">✓</span>
                        </div>
                        <span className="text-black/70 leading-relaxed flex-1">
                          {t(`legal.privacy.${section.key}.item${i + 1}`)}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}

                {section.special && (
                  <ul className="space-y-3 mt-4">
                    <li className="flex items-start gap-3 group">
                      <div className="flex-shrink-0 w-6 h-6 bg-black/5 rounded-full flex items-center justify-center mt-0.5 group-hover:bg-black group-hover:text-white transition-colors duration-300">
                        <span className="text-xs font-bold">✓</span>
                      </div>
                      <span className="text-black/70 leading-relaxed flex-1">
                        <strong className="text-black">Firebase/Google Cloud:</strong> {t('legal.privacy.sharing.firebase')}
                      </span>
                    </li>
                    <li className="flex items-start gap-3 group">
                      <div className="flex-shrink-0 w-6 h-6 bg-black/5 rounded-full flex items-center justify-center mt-0.5 group-hover:bg-black group-hover:text-white transition-colors duration-300">
                        <span className="text-xs font-bold">✓</span>
                      </div>
                      <span className="text-black/70 leading-relaxed flex-1">
                        <strong className="text-black">Resend:</strong> {t('legal.privacy.sharing.resend')}
                      </span>
                    </li>
                  </ul>
                )}
              </section>
            ))}

            {/* 7. Your Rights */}
            <section
              className={`
                mb-10 pb-8 border-b border-black/10
                transition-all duration-700 delay-700
                ${contentVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}
              `}
            >
              <div className="flex items-start gap-4 mb-4">
                <div className="flex-shrink-0 w-10 h-10 bg-black text-white rounded-lg flex items-center justify-center font-bold">
                  7
                </div>
                <h2 className="font-space text-2xl font-black text-black tracking-tight flex-1">
                  {t('legal.privacy.rights.title')}
                </h2>
              </div>

              <p className="text-black/70 leading-relaxed mb-4">
                {t('legal.privacy.rights.text')}
              </p>

              <ul className="space-y-3 mb-4">
                {['access', 'rectification', 'erasure', 'portability', 'objection', 'withdraw'].map((right) => (
                  <li key={right} className="flex items-start gap-3 group">
                    <div className="flex-shrink-0 w-6 h-6 bg-black/5 rounded-full flex items-center justify-center mt-0.5 group-hover:bg-black group-hover:text-white transition-colors duration-300">
                      <span className="text-xs font-bold">✓</span>
                    </div>
                    <span className="text-black/70 leading-relaxed flex-1">
                      <strong className="text-black">{t(`legal.privacy.rights.${right}`)}:</strong>{' '}
                      {t(`legal.privacy.rights.${right}Desc`)}
                    </span>
                  </li>
                ))}
              </ul>

              <p className="text-black/70 leading-relaxed">
                {t('legal.privacy.rights.exercise')}
              </p>
            </section>

            {/* 8-12: Remaining sections */}
            {[
              { num: 8, key: 'security', items: 3 },
              { num: 9, key: 'cookies', items: 0 },
              { num: 10, key: 'children', items: 0 },
              { num: 11, key: 'international', items: 0 },
              { num: 12, key: 'changes', items: 0 }
            ].map((section, idx) => (
              <section
                key={section.key}
                className={`
                  mb-10 pb-8 border-b border-black/10
                  transition-all duration-700
                  ${contentVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}
                `}
                style={{ transitionDelay: contentVisible ? `${(idx + 8) * 0.1}s` : '0s' }}
              >
                <div className="flex items-start gap-4 mb-4">
                  <div className="flex-shrink-0 w-10 h-10 bg-black text-white rounded-lg flex items-center justify-center font-bold">
                    {section.num}
                  </div>
                  <h2 className="font-space text-2xl font-black text-black tracking-tight flex-1">
                    {t(`legal.privacy.${section.key}.title`)}
                  </h2>
                </div>

                <p className="text-black/70 leading-relaxed mb-4">
                  {t(`legal.privacy.${section.key}.text`)}
                </p>

                {section.items > 0 && (
                  <ul className="space-y-3">
                    {[...Array(section.items)].map((_, i) => (
                      <li key={i} className="flex items-start gap-3 group">
                        <div className="flex-shrink-0 w-6 h-6 bg-black/5 rounded-full flex items-center justify-center mt-0.5 group-hover:bg-black group-hover:text-white transition-colors duration-300">
                          <span className="text-xs font-bold">✓</span>
                        </div>
                        <span className="text-black/70 leading-relaxed flex-1">
                          {t(`legal.privacy.${section.key}.item${i + 1}`)}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            ))}

            {/* 13. Contact */}
            <section
              className={`
                transition-all duration-700 delay-1000
                ${contentVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}
              `}
            >
              <div className="flex items-start gap-4 mb-4">
                <div className="flex-shrink-0 w-10 h-10 bg-black text-white rounded-lg flex items-center justify-center font-bold">
                  13
                </div>
                <h2 className="font-space text-2xl font-black text-black tracking-tight flex-1">
                  {t('legal.privacy.contact.title')}
                </h2>
              </div>

              <p className="text-black/70 leading-relaxed mb-4">
                {t('legal.privacy.contact.text')}
              </p>

              <div className="relative bg-white border-2 border-black/10 rounded-xl p-6 mb-4 group hover:border-black transition-all duration-300">
                <p className="font-bold text-black mb-4 text-lg">Logami</p>

                <div className="space-y-3">
                  <div className="flex items-center gap-3 text-black/70">
                    <div className="w-8 h-8 bg-black/5 rounded-lg flex items-center justify-center group-hover:bg-black group-hover:text-white transition-all duration-300">
                      <Mail className="w-4 h-4" />
                    </div>
                    <span className="font-medium">team@logami.app</span>
                  </div>

                  <div className="flex items-center gap-3 text-black/70">
                    <div className="w-8 h-8 bg-black/5 rounded-lg flex items-center justify-center group-hover:bg-black group-hover:text-white transition-all duration-300">
                      <Globe className="w-4 h-4" />
                    </div>
                    <span className="font-medium">https://logami.app</span>
                  </div>
                </div>

                <div className="absolute top-3 right-3 w-3 h-3 bg-black/10 rounded-full group-hover:bg-black transition-colors duration-300" />
              </div>

              <p className="text-black/70 leading-relaxed">
                {t('legal.privacy.contact.authority')}
              </p>
            </section>
          </div>
        </div>

        {/* Footer CTA - Enhanced */}
        <div
          ref={ctaRef}
          className={`
            relative bg-white border-[3px] border-black rounded-2xl p-8 text-center overflow-hidden group
            transition-all duration-1000 hover:shadow-2xl
            ${ctaVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}
          `}
        >
          <div className="absolute top-0 left-0 w-12 h-12 border-t-4 border-l-4 border-black/10 rounded-tl-2xl" />
          <div className="absolute bottom-0 right-0 w-12 h-12 border-b-4 border-r-4 border-black/10 rounded-br-2xl" />

          <div className="relative z-10">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-black rounded-2xl mb-4 group-hover:scale-110 group-hover:rotate-6 transition-all duration-500">
              <Shield className="w-8 h-8 text-white" />
            </div>

            <p className="text-lg text-black/70 mb-6 max-w-2xl mx-auto leading-relaxed">
              {t('legal.privacy.footer.text')}
            </p>

            <button
              onClick={() => navigate('/settings')}
              className="group/btn relative px-8 py-4 bg-black text-white font-space font-bold rounded-xl border-3 border-black overflow-hidden transition-all duration-500 hover:scale-105 hover:shadow-2xl"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-gray-800 via-black to-gray-800 opacity-0 group-hover/btn:opacity-100 transition-opacity duration-500" style={{
                backgroundSize: '200% 100%',
                animation: 'shimmer 3s infinite'
              }} />

              <span className="relative z-10 flex items-center justify-center gap-2">
                <span>{t('legal.privacy.footer.button')}</span>
                <ArrowLeft className="w-5 h-5 rotate-180 transition-transform duration-300 group-hover/btn:translate-x-1" />
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicyPage;